/**
 * Draft storage — nothing the user types is ever lost.
 *
 * Every text the user writes is kept locally while they write it, so a lost
 * connection, an accidental back, a phone call mid-form or an app update
 * never costs them their work. Drafts are per-device only: they never reach
 * the server (the server gets the data when the user actually saves).
 *
 * Privacy rules baked in, because this holds clinical text:
 *  - passwords and hidden fields are NEVER stored;
 *  - drafts are namespaced per account, so two people sharing a phone don't
 *    see each other's half-written notes;
 *  - they expire after DRAFT_TTL_DAYS and are wiped on logout.
 */

const PREFIX = 'ni.draft.v1';
const DRAFT_TTL_DAYS = 7;
const TTL_MS = DRAFT_TTL_DAYS * 24 * 60 * 60 * 1000;

/** Account scope. Kept dumb on purpose: a stale scope must never crash a form. */
function scope() {
  try {
    const raw = localStorage.getItem('nutrition_user_data');
    if (raw) {
      const u = JSON.parse(raw);
      if (u && (u.id || u.email)) return `u${u.id || u.email}`;
    }
  } catch (_) { /* corrupt payload — fall through to guest */ }
  return 'guest';
}

function keyFor(view, field) {
  return `${PREFIX}.${scope()}.${view}.${field}`;
}

export function saveDraft(view, field, value) {
  if (value === undefined || value === null || value === '') {
    return clearDraft(view, field);
  }
  try {
    localStorage.setItem(keyFor(view, field), JSON.stringify({ v: value, t: Date.now() }));
  } catch (_) {
    // Quota full or private mode: losing a draft is bad, breaking the form is
    // worse. Free what we can and give up silently.
    sweepExpired(true);
  }
}

export function readDraft(view, field) {
  try {
    const raw = localStorage.getItem(keyFor(view, field));
    if (!raw) return undefined;
    const { v, t } = JSON.parse(raw);
    if (!t || Date.now() - t > TTL_MS) {
      clearDraft(view, field);
      return undefined;
    }
    return v;
  } catch (_) {
    return undefined;
  }
}

export function clearDraft(view, field) {
  try { localStorage.removeItem(keyFor(view, field)); } catch (_) { /* noop */ }
}

/** Wipe every draft of one screen — call it after a successful save. */
export function clearViewDrafts(view) {
  const head = `${PREFIX}.${scope()}.${view}.`;
  forEachKey((k) => { if (k.startsWith(head)) localStorage.removeItem(k); });
}

/** Wipe everything for the current account (logout). */
export function clearAllDrafts() {
  forEachKey((k) => { if (k.startsWith(PREFIX)) localStorage.removeItem(k); });
}

/** How many drafts a screen is holding — powers the "sin guardar" hint. */
export function countViewDrafts(view) {
  const head = `${PREFIX}.${scope()}.${view}.`;
  let n = 0;
  forEachKey((k) => { if (k.startsWith(head)) n += 1; });
  return n;
}

export function sweepExpired(aggressive = false) {
  const now = Date.now();
  forEachKey((k) => {
    if (!k.startsWith(PREFIX)) return;
    try {
      const { t } = JSON.parse(localStorage.getItem(k) || '{}');
      if (!t || now - t > (aggressive ? TTL_MS / 7 : TTL_MS)) localStorage.removeItem(k);
    } catch (_) {
      localStorage.removeItem(k);
    }
  });
}

function forEachKey(fn) {
  try {
    // Snapshot first: removing during iteration reindexes localStorage.
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) keys.push(localStorage.key(i));
    keys.forEach((k) => k && fn(k));
  } catch (_) { /* private mode */ }
}

export default {
  saveDraft, readDraft, clearDraft, clearViewDrafts, clearAllDrafts,
  countViewDrafts, sweepExpired,
};
